import * as React from 'react';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import CheckIcon from '@material-ui/icons/CheckCircle';
import EditIcon from '@material-ui/icons/Edit';
import PaymentIcon from '@material-ui/icons/Payment';
import ReceiptIcon from '@material-ui/icons/Receipt';
import AssignmentIcon from '@material-ui/icons/Assignment';
import PersonIcon from '@material-ui/icons/Person';
import DeleteIcon from '@material-ui/icons/Delete';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import { useTranslations } from '../../store/translations/hooks';
import { TK } from '../../store/translations/translationKeys';
import { AppointmentData, PermissionEnum } from '../../models';
import { isPaid } from '../../shared/models-helpers/appointmentDataHelper';
import { getAppointmentTotalPayments, getTotalPrice } from '../../shared/appointmint/appointment';
import { useSelector } from 'react-redux';
import { ApplicationState } from '../../store';
import { canCreateInvoices, canSeePrices, canEditAppointments, getPermissions, isProfessional } from '../../store/session/selectors';
import { useGeneralSettings } from '../../store/settings/hooks';
import { PaymentLabel } from '../Forms/fields/PaymentsList';

export interface AppointmentContextMenuProps {
    mouseX: number
    mouseY: number
    appointment: AppointmentData
    onClose: () => void
    onEdit?: () => void
    onConfirm?: () => void
    onCreateInvoice?: () => void
    onOpenClient?: () => void
    onDelete?: () => void
    onSendSurvey?: () => void
    canSendSurvey?: boolean
    sendSurveyDisabledMessage?: string
    onEditPayment?: () => void
    onQuickPayment?: (paymentType: string) => void
    onOpenCashRegister?: () => void
}

const AppointmentContextMenu: React.FC<AppointmentContextMenuProps> = ({
    mouseX,
    mouseY,
    appointment,
    onClose,
    onEdit,
    onConfirm,
    onCreateInvoice,
    onOpenClient,
    onDelete,
    onSendSurvey,
    canSendSurvey,
    sendSurveyDisabledMessage,
    onEditPayment,
    onQuickPayment,
    onOpenCashRegister,
}) => {
    const t = useTranslations();
    const generalSettings = useGeneralSettings();

    const canConfirmAppointments = useSelector((state: ApplicationState) =>
        !generalSettings.doNotUseAppointmentConfirmation && !getPermissions(state).includes(PermissionEnum.DoNotAllowToConfirmAppointments))
    const canCreateOrEditAppointments = useSelector((state: ApplicationState) =>
        !getPermissions(state).includes(PermissionEnum.DoNotCreateOrEditAppointments))
    const canEditPayments = useSelector(canSeePrices)
    const canCreateInvoice = useSelector(canCreateInvoices)
    const canDeleteAppointments = useSelector((state: ApplicationState) =>
        (canEditAppointments(state) || isProfessional(state))
        && !getPermissions(state).includes(PermissionEnum.DoNotCreateOrEditAppointments))

    const [paymentSubmenu, setPaymentSubmenu] = React.useState<{ mouseX: number; mouseY: number } | null>(null)
    const [mainMenuOpen, setMainMenuOpen] = React.useState(true)

    const paymentMissingValue = React.useMemo(() => {
        const totalToPay = getTotalPrice(appointment) * (1 + (appointment.vat ?? 0))
        const totalPaid = getAppointmentTotalPayments(appointment)
        return Math.max(0, totalToPay - totalPaid)
    }, [appointment])

    const handleAddPaymentClick = () => {
        setPaymentSubmenu({ mouseX, mouseY })
        setMainMenuOpen(false)
    }

    const handleQuickPaymentClick = (paymentType: string) => {
        onQuickPayment?.(paymentType)
        setPaymentSubmenu(null)
        onClose()
    }

    const handleAdvancedPaymentClick = () => {
        onEditPayment?.()
        setPaymentSubmenu(null)
        onClose()
    }

    return (<>
        <Menu
            open={mainMenuOpen}
            onClose={onClose}
            anchorReference="anchorPosition"
            anchorPosition={{ top: mouseY, left: mouseX }}
        >
            {canConfirmAppointments && onConfirm && (
                <MenuItem onClick={() => { onConfirm(); onClose(); }}>
                    <ListItemIcon style={{minWidth: '2rem'}}>
                        <CheckIcon fontSize="small" style={{color: appointment.hasHappened ? 'var(--success-color)' : undefined}} />
                    </ListItemIcon>
                    {appointment.hasHappened ? t(TK.confirmed) : t(TK.confirm)}
                </MenuItem>
            )}
            {canCreateOrEditAppointments && onEdit && (
                <MenuItem onClick={() => { onEdit(); onClose(); }}>
                    <ListItemIcon style={{minWidth: '2rem'}}>
                        <EditIcon fontSize="small" />
                    </ListItemIcon>
                    {t(TK.edit)} {t(TK.appointment)}
                </MenuItem>
            )}
            {canEditPayments && (onEditPayment || onQuickPayment) && !isPaid(appointment) && (
                <MenuItem onClick={handleAddPaymentClick}>
                    <ListItemIcon style={{minWidth: '2rem'}}>
                        <PaymentIcon fontSize="small" />
                    </ListItemIcon>
                    {t(TK.add)} {t(TK.payment)}
                </MenuItem>
            )}
            {canCreateInvoice && onCreateInvoice && (
                <MenuItem onClick={() => { onCreateInvoice(); onClose(); }}>
                    <ListItemIcon style={{minWidth: '2rem'}}>
                        <ReceiptIcon fontSize="small" style={{color: (appointment.invoices?.length ?? 0) > 0 ? 'var(--success-color)' : undefined}} />
                    </ListItemIcon>
                    {t(TK.issueInvoice)}
                </MenuItem>
            )}
            {canSendSurvey !== undefined && onSendSurvey && (
                <MenuItem
                    onClick={() => { onSendSurvey(); onClose(); }}
                    disabled={!canSendSurvey}
                    title={!canSendSurvey ? (sendSurveyDisabledMessage ?? t(TK.clientContactRequiredForSurvey)) : undefined}
                >
                    <ListItemIcon style={{minWidth: '2rem'}}>
                        <AssignmentIcon fontSize="small" />
                    </ListItemIcon>
                    {t(TK.sendSurvey)}
                </MenuItem>
            )}
            {onOpenClient && appointment.clientId && (
                <MenuItem onClick={() => { onOpenClient(); onClose(); }}>
                    <ListItemIcon style={{minWidth: '2rem'}}>
                        <PersonIcon fontSize="small" />
                    </ListItemIcon>
                    {t(TK.open)} {t(TK.client)}
                </MenuItem>
            )}
            {onOpenCashRegister && appointment.clientId && (
                <MenuItem onClick={() => { onOpenCashRegister(); onClose(); }}>
                    <ListItemIcon style={{minWidth: '2rem'}}>
                        <PointOfSaleIcon fontSize="small" />
                    </ListItemIcon>
                    {t(TK.cashRegisterOpen)}
                </MenuItem>
            )}
            {canDeleteAppointments && onDelete && (
                <MenuItem onClick={() => { onDelete(); onClose(); }}>
                    <ListItemIcon style={{minWidth: '2rem'}}>
                        <DeleteIcon fontSize="small" style={{color: 'var(--danger-color)'}} />
                    </ListItemIcon>
                    {t(TK.cancelAppointment)}
                </MenuItem>
            )}
        </Menu>

        <Menu
            open={paymentSubmenu !== null}
            onClose={() => { setPaymentSubmenu(null); onClose(); }}
            anchorReference="anchorPosition"
            anchorPosition={paymentSubmenu ? { top: paymentSubmenu.mouseY, left: paymentSubmenu.mouseX } : undefined}
        >
            {paymentMissingValue > 0 && onQuickPayment && generalSettings.paymentMethods.map(method => (
                <MenuItem key={method} onClick={() => handleQuickPaymentClick(method)}>
                    <PaymentLabel paymentType={method} />
                </MenuItem>
            ))}
            {onEditPayment && (
                <MenuItem onClick={handleAdvancedPaymentClick}>
                    {t(TK.advanced)}...
                </MenuItem>
            )}
        </Menu>
    </>);
}

export default AppointmentContextMenu;
